import cloud1 from './assets/clouds/cloud-1.png';
import cloud2 from './assets/clouds/cloud-2.png';
import cloud3 from './assets/clouds/cloud-3.png';
import cloud4 from './assets/clouds/cloud-4.png';
import cloud5 from './assets/clouds/cloud-5.png';
import cloud6 from './assets/clouds/cloud-6.png';

import picharacter1 from './assets/character/pi-char-1.png';
import picharacter2 from './assets/character/pi-char-2.png';
import picharacter3 from './assets/character/pi-char-3.png';
import picharacter4 from './assets/character/pi-char-4.png';
import picharacter5 from './assets/character/pi-char-5.png';
import picharacter6 from './assets/character/pi-char-6.png';

import ground from './assets/ground/starting-ground.png';

import mountains from './assets/background/mountains.png';
import meadow from './assets/background/meadow.png';

import soundPath from './assets/sounds/correct.wav';
import soundOn from './assets/icons/sound-on.png';
import soundOff from './assets/icons/sound-off.png';

import piDigitsFile from './assets/pi-digits/100k-pi-digits.txt';

const GameState = {
    WAITING: 'waiting',
    RUNNING: 'running',
    FALLING: 'falling',
    REVEALING_DIGITS: 'revealing_digits',
    GAME_OVER: 'game_over'
}

class GameEngine {
    constructor(canvas) {
        this.state = GameState.WAITING;
        this.gameIsRunning = false;
        this.assetsLoaded = false;

        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.animationId = null;

        this.lastTime = 0;

        this.targetFPS = 30;
        this.frameInterval = 1000 / this.targetFPS;
        this.nextFrameTime = 0;

        this.cloudImages = [];
        this.clouds = []; // Array of currently spawned clouds
        this.lastCloudSpawnTime = 0;
        this.cloudSpawnRate = 1; // clouds per second

        this.startingGroundImage = null;
        this.mountainsImage = null;
        this.meadowImage = null;
        this.mountainsX = 0;
        this.meadowX = 0;

        this.characterFrames = []
        this.character = {
            x: 100,
            y: canvas.height - 115 - 100, // 115 pixels above bottom, minus character height
            width: 100,
            height: 100,
            phaseTime: 0, // total time which is then divided by number of frames and frame / phase duration
            phase: 0 // For frame shift, 6 frames total
        };

        this.planks = [];
        this.lastGuessedDigitIndex = -1;
        this.plankWidth = 100;
        this.startingPlankOffset = 400; // width of starting ground
        this.totalDistanceRun = 0;
        this.runningSpeed = 0.2; // pixels per millisecond

        this.inputEnabled = true;
        this.inputCooldown = 0; // ms
        this.cooldownOnMistake = 500; // ms
        this.currentInput = '';
        this.score = 0;
        this.highScore = this.loadHighScore();
        this.timeRevealingDigits = 0; // For REVEALING_DIGITS state to know which one was written and which one is next
        
        this.lastTimeMultiplier = 1;

        this.correctSound = null;
        this.soundOnIcon = null;
        this.soundOffIcon = null;
        this.soundIcon = {
            x: 30,
            y: 30,
            size: 32
        };
        this.soundEnabled = true;
        this.handleMouse();


    }

    async loadAssets() {
        const cloudImagePaths = [cloud1, cloud2, cloud3, cloud4, cloud5, cloud6];
        const promises1 = cloudImagePaths.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.cloudImages = await Promise.all(promises1);


        const characterFramesPaths = [picharacter1, picharacter2, picharacter3, picharacter4, picharacter5, picharacter6];
        const promises2 = characterFramesPaths.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.characterFrames = await Promise.all(promises2);

        const groundPaths = [ground];
        const promises3 = groundPaths.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.startingGroundImage = (await Promise.all(promises3))[0];

        const mountainPath = [mountains];
        const promises4 = mountainPath.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.mountainsImage = (await Promise.all(promises4))[0];

        const meadowPath = [meadow];
        const promises5 = meadowPath.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.meadowImage = (await Promise.all(promises5))[0];

        const soundOnPath = [soundOn];
        const promises6 = soundOnPath.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.soundOnIcon = (await Promise.all(promises6))[0];

        const soundOffPath = [soundOff];
        const promises7 = soundOffPath.map((path) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = path;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
        });
        this.soundOffIcon = (await Promise.all(promises7))[0];


        this.correctSound = new Audio(soundPath);
        this.correctSound.volume = 1;


        const response = await fetch(piDigitsFile);
        const text = await response.text();
        let piDigits = text.split('');

        for (let i = 0; i < piDigits.length; i++) {
            this.planks.push({
                digit: piDigits[i],
                x: i * this.plankWidth + this.startingPlankOffset + 1,
                y: this.canvas.height - 115,
                width: this.plankWidth - 2,
                height: 20,
                state: "empty" // empty or solid or revealed
            });
        }
    }

    loadHighScore() {
        try {
            const saved = localStorage.getItem('piRunnerHighScore');
            if (saved) {
                return parseInt(saved, 10);
            }
        } catch (error) {
            console.warn("Failed to load high score: ", error);
        }
        return 0;
    }

    saveHighScore() {
        try {
            localStorage.setItem('piRunnerHighScore', this.highScore.toString());
        } catch (error) {
            console.error("Failed to save high score: ", error);
        }
    }

    handleKeyPress(key) {
        if (!this.inputEnabled) {
            return; // Don't process input
        }
        this.processInput(key);
    }

    processInput(key) {
        const firstEmptyPlank = this.getFirstEmptyPlank();

        switch(this.state) {
            case GameState.WAITING: {
                if (!(key >= '0' && key <= '9')){
                    break;
                }

                if (key === firstEmptyPlank.digit){
                    this.state = GameState.RUNNING;
                    this.lastTime = performance.now();
                    firstEmptyPlank.state = "solid";
                    this.score++;
                    this.lastGuessedDigitIndex++;
                    this.playCorrectSound();
                }
                break;
            }

            case GameState.RUNNING: {
                if (!(key >= '0' && key <= '9')){
                    break;
                }

                if (key === firstEmptyPlank.digit) {
                    firstEmptyPlank.state = "solid";
                    this.score++;
                    this.lastGuessedDigitIndex++;
                    this.playCorrectSound();
                }
                else {
                    this.inputCooldown = this.cooldownOnMistake;
                    this.inputEnabled = false;
                }
                break;
            }

            case GameState.GAME_OVER: {

                if (!(key.length === 1 || key === 'Enter' || key === ' ')) { // so that CTRL, Alt, PRT SC and similar don't restart game
                    break;
                }

                this.state = GameState.WAITING;

                this.lastTime = 0;
                this.nextFrameTime = 0;
                this.clouds = []; // Array of currently spawned clouds
                this.lastCloudSpawnTime = 0;
                this.character = {
                    x: 100,
                    y: this.canvas.height - 115 - 100, // 115 pixels above bottom, minus character height
                    width: 100,
                    height: 100,
                    phaseTime: 0, // total time which is then divided by number of frames and frame / phase duration
                    phase: 0 // For frame shift, 6 frames total
                };

                for (let i = 0; i < this.planks.length; i++) {
                    if (this.planks[i].state !== "empty"){
                        this.planks[i].state = "empty";
                    }
                    else {
                        break;
                    }
                }
                this.lastGuessedDigitIndex = -1;
                this.totalDistanceRun = 0;
                this.score = 0;
                this.timeRevealingDigits = 0;
                this.mountainsX = 0;
                this.meadowX = 0;
                this.lastTimeMultiplier = 1;

                this.start();
                break;
            }

            default: {
                break;
            }
        }
    }

    handleMouse() {
        this.canvas.addEventListener('click', (event) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            // Check if click was on sound icon
            if (x >= this.soundIcon.x && x <= this.soundIcon.x + this.soundIcon.size && 
                y >= this.soundIcon.y && y <= this.soundIcon.y + this.soundIcon.size) {
                    this.soundEnabled = !this.soundEnabled;
                };
        });
    }

    getFirstEmptyPlank() {
       return this.planks[this.lastGuessedDigitIndex + 1];
    }

    spawnCloud() {
        if (this.cloudImages.length === 0) return; // No images loaded yet
        const randomCloud = this.cloudImages[Math.floor(Math.random() * this.cloudImages.length)];
        const randomY = (Math.random() * 100);
        const randomWidth = 50 + Math.random() * 100; // Width between 50 and 150
        this.clouds.push({
            image: randomCloud,
            x: this.canvas.width,
            y: randomY,
            width: randomWidth,
            height: randomWidth * (randomCloud.height / randomCloud.width)
        });
    }

    prespawnClouds(){
        for (let i = 0; i < 10; i++){
            const randomCloud = this.cloudImages[Math.floor(Math.random() * this.cloudImages.length)];
            const randomX = (Math.random() * this.canvas.width)
            const randomY = (Math.random() * 100);
            const randomWidth = 50 + Math.random() * 100; // Width between 50 and 150
            this.clouds.push({
                image: randomCloud,
                x: randomX,
                y: randomY,
                width: randomWidth,
                height: randomWidth * (randomCloud.height / randomCloud.width)
            });
        }
    }

    updateClouds(deltaTime) {
        const speed = 0.1; // pixels per millisecond
        for (let cloud of this.clouds) {
            cloud.x -= speed * deltaTime;
        }

        // Remove clouds that have moved off screen
        for (let i = this.clouds.length - 1; i >= 0; i--) {
            if (this.clouds[i].x + this.clouds[i].width < 0) {
                this.clouds.splice(i, 1);
            }
        }
    }

    updateCharacter(deltaTime) {
        const phaseDuration = 100; // milliseconds per phase
        const numberOfFrames = this.characterFrames.length;
        this.character.phaseTime += deltaTime;
        this.character.phase = Math.floor(this.character.phaseTime / phaseDuration % numberOfFrames);
    }

    updateFallingCharacter(deltaTime) {
        this.character.x += this.runningSpeed * deltaTime;
        this.character.y += this.runningSpeed * deltaTime;
        if (this.character.y > this.canvas.height){
            this.state = GameState.REVEALING_DIGITS;
        }
    }

    updatePlanks(deltaTime) {
        this.totalDistanceRun += this.runningSpeed * deltaTime;
        let firstEmptyPlankIndex = this.lastGuessedDigitIndex + 1;
        let emptyPlankX = firstEmptyPlankIndex * this.plankWidth + this.startingPlankOffset;
        if (this.totalDistanceRun + this.character.x > emptyPlankX) {
            // Character starts falling
            this.state = GameState.FALLING;
        }
    }

    updateGame(currentTime = 0){
        switch (this.state) {

            case GameState.WAITING: {
                this.lastTime = 0;
                currentTime = 0;

                this.drawBackground(0);
                this.drawClouds();
                this.drawStartingGround();
                this.drawPlanks();
                this.drawCharacter();
                this.drawScore();
                this.drawSoundIcon();
                break;
            }

            case GameState.RUNNING: {
                let deltaTime = currentTime - this.lastTime;
                this.lastTime = currentTime;

                // Input cooldown handling
                if (this.inputCooldown > 0) {
                    this.inputCooldown -= deltaTime;
                    if (this.inputCooldown <= 0) {
                        this.inputCooldown = 0;
                        this.inputEnabled = true;
                    }
                }

                const timeMultiplier = this.getTimeMultiplier();
                deltaTime = deltaTime * timeMultiplier;

                const chanceToSpawn = this.cloudSpawnRate * (deltaTime / 1000);
                if (Math.random() < chanceToSpawn) {
                    this.spawnCloud();
                }
                this.updateClouds(deltaTime);
                this.updatePlanks(deltaTime);
                this.updateCharacter(deltaTime);

                this.drawBackground(deltaTime);
                this.drawClouds();
                this.drawStartingGround();
                this.drawPlanks();
                this.drawCharacter();
                this.drawCoolDownCircle();
                this.drawScore();
                this.drawSoundIcon();
                break;
            }

            case GameState.FALLING: {
                const deltaTime = currentTime - this.lastTime;
                this.lastTime = currentTime;

                const chanceToSpawn = this.cloudSpawnRate * (deltaTime / 1000);
                if (Math.random() < chanceToSpawn) {
                    this.spawnCloud();
                }
                this.updateClouds(deltaTime);
                this.updateFallingCharacter(deltaTime);

                this.drawBackground(deltaTime);
                this.drawClouds();
                this.drawStartingGround();
                this.drawPlanks();
                this.drawCharacter();
                this.drawScore();
                break;
            }

            case GameState.REVEALING_DIGITS: {
                const deltaTime = currentTime - this.lastTime;
                this.lastTime = currentTime;
                this.drawGameOverDigits(deltaTime);
                break;
            }

            case GameState.GAME_OVER: {
                this.inputCooldown = 0; // make sure player can continue
                this.inputEnabled = true;
                if (this.score > this.highScore){
                    this.highScore = this.score;
                    this.saveHighScore();
                }
                this.drawGameOver();
                this.stop();
                break;
            }

            default: {
                break;
            }
        }
    }

    getTimeMultiplier() {
        let range = this.calculateVisiblePlankIndices();
        let endIndex = range.endIndex;

        if (this.lastGuessedDigitIndex > endIndex - 2){
            this.lastTimeMultiplier *= 1.1;
        }
        else {
            this.lastTimeMultiplier = 1;
        }
        return this.lastTimeMultiplier;
    }

    drawBackground(deltaTime) {
        const mountainsSpeed = 0.04;
        const mountainsWidth = 642 * 3;
        const mountainsHeight = 43 * 3;
        const meadowWidth = 1022 * 2;
        const meadowHeight = 156 * 2;
        const meadowSpeed = 0.08;

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.fillStyle = '#87CEEB'; // Sky blue
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        this.mountainsX -= mountainsSpeed * deltaTime;
        this.meadowX -= meadowSpeed * deltaTime;

        while (this.mountainsX < mountainsWidth * -1) {
            this.mountainsX += mountainsWidth;
        }
        while (this.meadowX < meadowWidth * -1) {
            this.meadowX += meadowWidth;
        }

        for (let i = 0; i < 3; i++) {
            this.ctx.drawImage(this.mountainsImage, this.mountainsX + (mountainsWidth * i), this.canvas.height - meadowHeight - 17, 
                mountainsWidth, mountainsHeight);
        }
        for (let i = 0; i < 3; i++) {
            this.ctx.drawImage(this.meadowImage, this.meadowX + (meadowWidth * i), this.canvas.height - meadowHeight, 
                    meadowWidth, meadowHeight);
        }
    }

    drawClouds() {
        for (let cloud of this.clouds) {
            this.ctx.drawImage(cloud.image, cloud.x, cloud.y, cloud.width, cloud.height);
        }
    }

    drawCharacter() {
        this.ctx.drawImage(this.characterFrames[this.character.phase], this.character.x, this.character.y, this.character.width, this.character.height);
    }

    drawPlanks() {
        // First calculate which planks are visible
        let range = this.calculateVisiblePlankIndices();
        let startIndex = range.startIndex;
        let endIndex = range.endIndex;
        if (startIndex < 0) {
            startIndex = 0;
        }
        for (let i = startIndex; i <= endIndex; i++) {
            if (this.planks[i].state === "empty") {
                this.ctx.fillStyle = "grey";
                this.ctx.fillRect(this.planks[i].x - this.totalDistanceRun, this.planks[i].y, this.planks[i].width, this.planks[i].height);
            }
            else {
                this.ctx.fillStyle = "brown";
                this.ctx.fillRect(this.planks[i].x - this.totalDistanceRun, this.planks[i].y, this.planks[i].width, this.planks[i].height);
                this.drawDigit(this.planks[i].digit, this.planks[i].x - this.totalDistanceRun + this.planks[i].width / 2, this.planks[i].y - 20, this.plankWidth);
            }
        }
    }

    drawStartingGround() {
        const baseX = 0;
        const baseY = this.canvas.height - 122;
        const baseWidth = 400;
        const baseHeight = 122;

        if (this.totalDistanceRun <= baseWidth){
            this.ctx.drawImage(this.startingGroundImage, baseX - this.totalDistanceRun, baseY, baseWidth, baseHeight);
            this.drawDigit(".", this.startingPlankOffset - this.totalDistanceRun - this.plankWidth / 2, baseY - 20, this.plankWidth);
            this.drawDigit("3", this.startingPlankOffset - this.totalDistanceRun - this.plankWidth * 3 / 2, baseY - 20, this.plankWidth);
        }
    }

    drawGameOverDigits(deltaTime){
        let range = this.calculateVisiblePlankIndices();
        let startIndex = this.lastGuessedDigitIndex + 1;
        let endIndex = range.endIndex;
        const revealingSpeed = 150; // ms per digit

        let firstUnrevealedPlankIndex = -1;
        for (let i = startIndex; i <= endIndex; i++){
            if (this.planks[i].state === "empty"){
                firstUnrevealedPlankIndex = i;
                break;
            }
        }
        if (firstUnrevealedPlankIndex !== -1){
            this.timeRevealingDigits += deltaTime;
            if ((firstUnrevealedPlankIndex - startIndex + 1) * revealingSpeed <= this.timeRevealingDigits){
                const plank = this.planks[firstUnrevealedPlankIndex];
                plank.state = "revealed";
                this.drawDigit(plank.digit, plank.x - this.totalDistanceRun + plank.width / 2, plank.y - 20, this.plankWidth);
                this.playCorrectSound();
            }
        }
        else {
            this.state = GameState.GAME_OVER;
        }
    }

    drawDigit(digit, x, y, size) {
        this.ctx.save();

        this.ctx.font = `bold ${size}px "EditUndo", monospace`;
        this.ctx.textAlign = "center";
        this.ctx.baseline = "middle";

        // Red outline
        this.ctx.lineWidth = 8;
        this.ctx.strokeStyle = "red";
        this.ctx.strokeText(digit, x, y);
        
        // Yellow fill
        this.ctx.fillStyle = "yellow";
        this.ctx.fillText(digit, x, y);
        
        this.ctx.restore();
    }

    drawScore() {
        const scoreX = this.canvas.width - 280;
        const scoreY = 50;
        const fontSize = 48;

        // "Score: "
        this.ctx.font = `${fontSize}px "EditUndo", monospace`;
        this.ctx.fillStyle = 'yellow';
        this.ctx.textAlign = 'right';
        this.ctx.fillText('Score: ', scoreX, scoreY);

        // Digits
        const scoreStr = this.score.toString();
        for (let i = 0; i < scoreStr.length; i++) {
            const digit = scoreStr[i];
            const digitX = this.canvas.width - fontSize - (scoreStr.length - i - 1) * (fontSize);
            this.drawDigit(digit, digitX, scoreY, fontSize);
        }

        // High score
        this.ctx.font = `${fontSize}px "EditUndo", monospace`;
        this.ctx.fillStyle = 'yellow';
        this.ctx.textAlign = 'right';
        this.ctx.fillText('High score: ', scoreX, scoreY + fontSize);

        // High score digits
        const highScoreStr = this.highScore.toString();
        for (let i = 0; i < highScoreStr.length; i++) {
            const digit = highScoreStr[i];
            const digitX = this.canvas.width - fontSize - (highScoreStr.length - i - 1) * (fontSize);
            this.drawDigit(digit, digitX, scoreY + fontSize, fontSize);
        }

    }

    drawCoolDownCircle() {
        if (this.inputEnabled) {
            return;
        }

        const radius = 25;
        const progress = this.inputCooldown / this.cooldownOnMistake;
        const x = this.canvas.width - radius - 40;
        const y = this.canvas.height - radius - 40;

        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 
            - Math.PI / 2, (- Math.PI / 2) + (Math.PI * 2 * progress), false);
        this.ctx.lineTo(x, y);
        this.ctx.closePath();
        this.ctx.fillStyle = 'yellow';
        this.ctx.fill();
    }

    drawSoundIcon() {
        if (this.soundEnabled) {
            this.ctx.drawImage(this.soundOnIcon, this.soundIcon.x, this.soundIcon.y, this.soundIcon.size, this.soundIcon.size);
        }
        else {
            this.ctx.drawImage(this.soundOffIcon, this.soundIcon.x, this.soundIcon.y, this.soundIcon.size, this.soundIcon.size);
        }
    }

    drawGameOver(){
        this.ctx.save();
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        this.ctx.font = 'bold 64px "EditUndo", monospace';
        this.ctx.fillStyle = 'yellow';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText("Game Over", this.canvas.width / 2, this.canvas.height / 2 - 50);

        this.ctx.lineWidth = 2;
        this.ctx.strokeStyle = "red";
        this.ctx.strokeText("Game Over", this.canvas.width / 2, this.canvas.height / 2 - 50);

        this.ctx.fillStyle = 'white';
        this.ctx.font = 'bold 32px "EditUndo", monospace';
        this.ctx.shadowBlur = 5;
        this.ctx.fillText('Press any key to start', this.canvas.width / 2, this.canvas.height / 2 + 30);

        this.ctx.restore();
    }

    calculateVisiblePlankIndices() {
        let startIndex = Math.floor((this.totalDistanceRun - this.startingPlankOffset) / this.plankWidth);
        let endIndex = startIndex + Math.floor(this.canvas.width / this.plankWidth) + 1;
        return {
            startIndex: startIndex,
            endIndex: endIndex
        };
    }


    playCorrectSound() {
        if (!this.soundEnabled) return;
        this.correctSound.currentTime = 0;
        this.correctSound.play();
    }
    

    async start() { 
        this.gameIsRunning = true;
        if (!this.assetsLoaded) {
            await this.loadAssets();
            this.assetsLoaded = true;
        }
        this.prespawnClouds();
        this.lastTime = performance.now();
        this.gameLoop(performance.now());
    }

    stop() {
        this.gameIsRunning = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    gameLoop(currentTime = 0) {

        if (!this.gameIsRunning){
            return;
        }

        // For limiting FPS
        if (this.nextFrameTime === 0) {
            this.nextFrameTime = currentTime;
        }
        if (currentTime < this.nextFrameTime){
            this.animationId = requestAnimationFrame(() => this.gameLoop(performance.now()));
            return;
        }
        while (currentTime >= this.nextFrameTime) {  
            this.nextFrameTime += this.frameInterval;
        }

        this.updateGame(currentTime);
        this.animationId = requestAnimationFrame(() => this.gameLoop(performance.now()));
    }
}



export function createGameEngine(canvas) {
    return new GameEngine(canvas);
}